import { ParameterElastic_Schema } from "@/services/parameters/parametersSchema";
import { FilterDefinition_Schema } from "@/services/filter";
import { ApiResponse_Image_Schema } from "@/services/images";
import { MarketplaceItemElastic_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import { UserId_Schema } from "@/services/user/userIdSchema";
import { Nickname_Schema } from "@/services/user/profile";
import { Rating_Schema } from "@/services/user/rating";
import { DateTimeIso8601Local_Schema, WholeNumber_Schema } from "@/utilities/schema";
import { z } from "zod";
import { CategoryId_Schema } from "@/services/collection/categories/categoriesSchema";
import { Theme_Schema } from "@/services/themes/themeSchema";
import { CollectibleElastic_Schema } from "@/services/collectibles/elastic";

export const UserStanding_Schema = z
  .object({
    score: WholeNumber_Schema,
    rank: WholeNumber_Schema,
  })
  .meta({ openApi: "UserStanding" });

export const OwnerElastic_Schema = z
  .object({
    id: UserId_Schema,
    nickname: Nickname_Schema,
    rating: Rating_Schema,
    ratingCount: WholeNumber_Schema,
    avatarUrl: z.string().transform((val) => (val === "" ? null : val)),
    totalCollectionCount: WholeNumber_Schema,
    userStanding: UserStanding_Schema.nullable(),
  })
  .transform(({ totalCollectionCount, userStanding, ...rest }) => ({
    ...rest,
    itemsCount: totalCollectionCount, // Just to align it with UserPublicInfo - you can drop this when UserPublicInfo is not used anymore
    standing: userStanding,
  }));

export const CollectionItemElastic_Schema = z
  .object({
    itemId: WholeNumber_Schema,
    name: z.string(),
    collectibleName: z.string(),
    collectibleElastic: CollectibleElastic_Schema.nullable(),
    description: z.string(),
    note: z.string(),
    category: CategoryId_Schema,
    theme: Theme_Schema.nullable(),
    tags: z.array(z.string()),
    identification: z.array(z.string()),
    images: z.array(ApiResponse_Image_Schema),
    // API may send an object (empty or keyed by string) instead of an array — normalize to array
    parameters: z.preprocess((val) => (val && typeof val === "object" && !Array.isArray(val) ? Object.values(val) : val), ParameterElastic_Schema.array()),
    owner: OwnerElastic_Schema,
    added: z.string(),
    score: z.number(),
    marketPlaceItem: MarketplaceItemElastic_Schema.nullable(),
    likedByCurrentUser: z.boolean().nullable(),
    likesCount: WholeNumber_Schema,
    publishedDate: DateTimeIso8601Local_Schema,
  })
  .meta({
    apiSchema: "\\App\\Model\\Collection\\CollectionItemElastic",
  })
  .transform(({ marketPlaceItem, itemId, likedByCurrentUser, category, added, publishedDate, collectibleElastic, ...rest }) => ({
    ...rest,
    collectible: collectibleElastic,

    /**
     * You should never read this property directly. You should use useCollectionItemElastic() to extract marketplace item information.
     * Bacause Collection Item can have Marketplace item even if it is not for sale!!
     **/
    marketplaceItem: marketPlaceItem,

    id: itemId,
    likedByCurrentUser: likedByCurrentUser ?? false, // We don't want to work with null

    /** @deprecated Keep it for backward compatibility. Use categoryId instead */
    category,

    categoryId: category,

    dateAdded: added,
    datePublished: publishedDate,
  }));

export const CollectionItemElasticSearchResponse_Schema = z.object({
  totalItems: WholeNumber_Schema,
  items: z.array(CollectionItemElastic_Schema),
  parameters: z.array(FilterDefinition_Schema),
});

export type OwnerElastic = z.infer<typeof OwnerElastic_Schema>;
export type CollectionItemElastic = z.infer<typeof CollectionItemElastic_Schema>;
export type CollectionItemElasticSearchResponse = z.infer<typeof CollectionItemElasticSearchResponse_Schema>;
